import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  UserPlus,
  Edit,
  Trash2,
  Mail,
  Phone,
  Star,
  CheckCircle,
  XCircle,
  TrendingUp
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDrivers = () => {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    status: 'active'
  });

  useEffect(() => {
    const adminUser = localStorage.getItem('adminUser');
    if (!adminUser) {
      navigate('/admin/login');
      return;
    }
    fetchDrivers();
  }, [navigate]);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/drivers`);
      setDrivers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des chauffeurs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingDriver) {
        // Update existing driver
        await axios.put(`${API}/drivers/${editingDriver.id}`, formData);
        toast.success('Chauffeur mis à jour avec succès');
      } else {
        // Create new driver
        await axios.post(`${API}/drivers`, formData);
        toast.success('Chauffeur ajouté avec succès');
      }
      
      setShowAddDialog(false);
      setEditingDriver(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        status: 'active'
      });
      fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setFormData({
      firstName: driver.firstName,
      lastName: driver.lastName,
      email: driver.email,
      phone: driver.phone,
      status: driver.status
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (driverId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce chauffeur ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/drivers/${driverId}`);
      toast.success('Chauffeur supprimé avec succès');
      fetchDrivers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setEditingDriver(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      status: 'active'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-md border-b border-gold/20">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-gold to-amber-600 rounded-lg flex items-center justify-center">
                <Star className="w-6 h-6 text-black" fill="currentColor" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gold">Gestion des Chauffeurs</h1>
                <p className="text-sm text-gray-400">Luxury Transfer Service</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="outline"
              className="border-gold/30 text-gold hover:bg-gold/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au Dashboard
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8 pt-24">
        {/* Add Driver Button */}
        <div className="flex justify-end mb-6">
          <Dialog open={showAddDialog} onOpenChange={(open) => {
            setShowAddDialog(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-gold to-amber-600 text-black hover:from-amber-600 hover:to-gold">
                <UserPlus className="w-4 h-4 mr-2" />
                Ajouter un Chauffeur
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gray-900 border-gold/30 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="text-gold text-2xl">
                  {editingDriver ? 'Modifier le Chauffeur' : 'Ajouter un Chauffeur'}
                </DialogTitle>
                <DialogDescription className="text-gray-400">
                  Remplissez les informations du chauffeur ci-dessous
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-white">Prénom *</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="bg-gray-800 border-gold/30 text-white"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-white">Nom *</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="bg-gray-800 border-gold/30 text-white"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-gray-800 border-gold/30 text-white"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white">Téléphone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-gray-800 border-gold/30 text-white"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status" className="text-white">Statut</Label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-800 border border-gold/30 rounded-md text-white"
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddDialog(false);
                      resetForm();
                    }}
                    className="flex-1 border-gold/30 text-gold hover:bg-gold/10"
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-gold to-amber-600 text-black hover:from-amber-600 hover:to-gold"
                  >
                    {editingDriver ? 'Mettre à jour' : 'Ajouter'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Drivers List */}
        {loading ? (
          <div className="text-center text-white py-12">Chargement...</div>
        ) : drivers.length === 0 ? (
          <Card className="bg-gradient-to-br from-gray-900 to-black border-gold/30">
            <CardContent className="py-12 text-center">
              <p className="text-gray-400 text-lg">Aucun chauffeur enregistré</p>
              <p className="text-gray-500 text-sm mt-2">Cliquez sur "Ajouter un Chauffeur" pour commencer</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {drivers.map((driver) => (
              <Card key={driver.id} className="bg-gradient-to-br from-gray-900 to-black border-gold/30 hover:border-gold/50 transition-all">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-white text-xl">
                        {driver.firstName} {driver.lastName}
                      </CardTitle>
                      <p className="text-gray-400 text-sm mt-1">ID: {driver.id}</p>
                    </div>
                    <Badge
                      className={driver.status === 'active' 
                        ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                        : 'bg-red-500/20 text-red-400 border-red-500/30'
                      }
                    >
                      {driver.status === 'active' ? (
                        <><CheckCircle className="w-3 h-3 mr-1" /> Actif</>
                      ) : (
                        <><XCircle className="w-3 h-3 mr-1" /> Inactif</>
                      )}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center text-gray-300">
                    <Mail className="w-4 h-4 mr-2 text-gold" />
                    <span className="text-sm">{driver.email}</span>
                  </div>
                  <div className="flex items-center text-gray-300">
                    <Phone className="w-4 h-4 mr-2 text-gold" />
                    <span className="text-sm">{driver.phone}</span>
                  </div>
                  <div className="flex gap-2 pt-3">
                    <Button
                      onClick={() => handleEdit(driver)}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-gold/30 text-gold hover:bg-gold/10"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Modifier
                    </Button>
                    <Button
                      onClick={() => navigate(`/admin/commissions?driverId=${driver.id}`)}
                      variant="outline"
                      size="sm"
                      className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                      title="Voir Commissions"
                    >
                      <TrendingUp className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(driver.id)}
                      variant="outline"
                      size="sm"
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDrivers;
